import {Component, EventEmitter, inject, Input, OnInit, Output, OnChanges, SimpleChanges} from '@angular/core';
import {AuthService, DataSources, EmployeeBalanceDto, GetWorkingDaysQuery, ScreenService, TimeOffClient, TimeOffStatus} from "@services";
import notify from "devextreme/ui/notify";
import {BehaviorSubject, combineLatest, Observable, of} from "rxjs";
import {distinctUntilChanged, switchMap} from "rxjs/operators";

@Component({
    selector: 'app-time-off-popup',
    imports: [],
    template: ''
})
class TimeOffPopupComponent<T extends { status?: TimeOffStatus; employee?: { id?: string } }> implements OnInit, OnChanges {
    protected dataSources = inject(DataSources);
    protected screen = inject(ScreenService);
    protected timeOffClient = inject(TimeOffClient);
    protected auth = inject(AuthService);

    @Input() request: T;
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onAdd = new EventEmitter<void>();
    @Output() onStatusChange = new EventEmitter<TimeOffStatus>();

    employeesDataSource = this.dataSources.employeeOptions();
    formData: T | null = null;
    canShowInSelectBox: boolean
    employeeId: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    startDate: BehaviorSubject<Date | null> = new BehaviorSubject<Date | null>(null);
    endDate: BehaviorSubject<Date | null> = new BehaviorSubject<Date | null>(null);

    employeeBalance: Observable<EmployeeBalanceDto> = this.employeeId.pipe(
        distinctUntilChanged(),
        switchMap(id => {
            return id ? this.timeOffClient.getEmployeeBalance(id) : of(new EmployeeBalanceDto());
        })
    );

    daysRequested: Observable<number> = combineLatest([this.startDate, this.endDate, this.employeeId]).pipe(
        switchMap(([start, end, id]) => {
            if (start && end && id) {
                return this.timeOffClient.getWorkingDays(new GetWorkingDaysQuery({startDate: start, endDate: end, employeeId: id}));
            }
            return of(0);
        })
    );

    async ngOnInit(): Promise<void> {
        this.canShowInSelectBox = !!await this.employeesDataSource.byKey(this.auth.userId);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && changes['visible'].currentValue) {
            this.loadData();
        }
    }

    loadData() {
    }

    close() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
        this.formData = null;
    }

    handleSuccess() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
        this.onAdd.emit();
    }

    handleError(errors: any) {
        errors.forEach(((errMsg: any) => {
            notify({
                message: errMsg.message,
                type: "error",
                displayTime: 5000,
            }, {
                position: "bottom right",
                direction: "up-push"
            });
        }));
    }

    protected get canEdit(): boolean {
        return this.formData?.status === undefined;
    };

    protected get canApprove(): boolean {
        return this.formData?.status == TimeOffStatus.Pending;
    };

    protected get canDecline(): boolean {
        return this.formData?.status == TimeOffStatus.Pending;
    };

    protected get canWithdrawn(): boolean {
        return this.formData?.status == TimeOffStatus.Pending && this.formData?.employee.id == this.auth.userId;
    };

    protected onEmployeeChange(id: string) {
        this.employeeId.next(id);
        this.formData.employee.id = id;
    }

    protected onStartDateChange(date: string | number | Date) {
        this.startDate.next(date as Date);
    }

    protected onEndDateChange(date: string | number | Date) {
        this.endDate.next(date as Date);
    }

    protected get canEditEmployee(): boolean{
        return this.canEdit;
    }
}

export default TimeOffPopupComponent
